import type { ScenarioTemplateModel } from '@/entities/settings-template'
import type {
	SettingsTemplateCreateModel,
	SettingsTemplateModel,
	SettingsTemplateUpdateModel,
} from '@/shared/generated'
import type { UseFormReturn } from 'react-hook-form'

import React, { createContext, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { any, pickAll, prop } from 'lodash/fp'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useSettingsSchemasQuery } from '@/entities/core'
import {
	useCreateSettingsTemplateMutation,
	useSettingsTemplateQuery,
	useUpdateSettingsTemplateMutation,
} from '@/entities/settings-template'

import { ampli } from '@/shared/lib/ampli'
import { ajvResolver } from '@/shared/lib/validator/ajv'
import { getResolvedSchema } from '@/shared/tools/json-schema-utils'

export default function TemplateBuilderProvider({
	children,
	templateId,
}: {
	templateId?: string
	children: React.ReactNode
}) {
	const { t } = useTranslation()

	const isNew = !templateId
	const queryClient = useQueryClient()
	const templateType = 'scenario'
	useEffect(() => {
		ampli.builderOpen()
	}, [])
	const [activeType, setActiveType] = useState<string | null>(null)
	const schemaQuery = useSettingsSchemasQuery({
		select: getResolvedSchema(`template:${templateType}`),
	})
	const template = useSettingsTemplateQuery(templateId!, {
		enabled: !!templateId,
	})
	const createTemplateMutation = useCreateSettingsTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['settings-templates'],
			})
		},
	})
	const updateTemplateMutation = useUpdateSettingsTemplateMutation(
		templateId!,
		{
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ['settings-templates'],
				})
			},
		}
	)
	const methods = useForm<Omit<ScenarioTemplateModel, 'template_type'>>({
		resolver(values, context, options) {
			return ajvResolver(schemaQuery.data!, {
				strict: false,
			})(
				{
					...values,
					body: {
						...values.body,
						name: values.name,
					},
				},
				context,
				options
			)
		},
	})
	useEffect(() => {
		if (template.data) {
			methods.reset({
				name: template.data?.name,
				description: template.data?.description || '',
				body: template.data?.body,
			})
		} else if (!template.data && schemaQuery.data) {
			methods.reset({
				name: '',
				body: {
					name: '',
					settings: {
						stop_list: undefined,
						strategy: undefined,
						business: undefined,
					},
					pricing_campaigns: [],
				},
			})
			//getSchemaDefaultState(schemaQuery.data)
		}
	}, [template.data, schemaQuery.data])
	const isPending = any(prop('isPending'), [
		createTemplateMutation,
		updateTemplateMutation,
	])
	const isLoading = any(prop('isLoading'), [schemaQuery, template])
	const handleReset = () => {
		ampli.builderReset()
		methods.reset()
	}
	const handleSubmit = methods.handleSubmit(
		async (data: SettingsTemplateCreateModel | SettingsTemplateUpdateModel) => {
			setActiveType(null)
			if (isNew) {
				ampli.builderSave({
					is_new: true,
				})
				await createTemplateMutation.mutateAsync(
					{
						...data,
						body: {
							name: data.name,
							...data.body,
						},
						template_type: templateType,
					} as SettingsTemplateCreateModel,
					{
						onSuccess: () =>
							toast.success(
								t('toast.entity.created', { entity: t('entity.template') }),
								{
									duration: 5000,
									id: 'settigs-template',
								}
							),
					}
				)
			} else {
				ampli.builderSave({
					is_new: false,
				})
				await updateTemplateMutation.mutateAsync(
					{
						...data,
						body: {
							name: data.name,
							...data.body,
						},
					} as SettingsTemplateUpdateModel,
					{
						onSuccess: () =>
							toast.success(
								t('toast.entity.updated', { entity: t('entity.template') }),
								{
									duration: 5000,
									id: 'settigs-template',
								}
							),
					}
				)
			}
		}
	)

	return (
		<TemplateBuilderContext
			value={{
				isNew,
				template: template.data,
				meta: pickAll(
					['created_at', 'updated_at', 'created_by', 'updated_by'],
					template.data || {}
				),
				methods,
				isPending,
				isLoading,
				handleReset,
				handleSubmit,
				activeType,
				setActiveType,
			}}
		>
			{children}
		</TemplateBuilderContext>
	)
}
type TemplateBuilderContextType = {
	isNew: boolean
	template?: SettingsTemplateModel
	meta?: Partial<
		Pick<
			ScenarioTemplateModel,
			'created_at' | 'updated_at' | 'created_by' | 'updated_by'
		>
	>
	isPending: boolean
	isLoading: boolean
	methods: UseFormReturn<Omit<ScenarioTemplateModel, 'template_type'>>
	handleSubmit: () => void
	handleReset: () => void
	activeType: string | null
	setActiveType: (type: string | null) => void
}
export const TemplateBuilderContext = createContext<TemplateBuilderContextType>(
	{
		isNew: true,
		isPending: false,
		isLoading: true,
		methods: {} as UseFormReturn<Omit<ScenarioTemplateModel, 'template_type'>>,
		handleSubmit: () => {},
		handleReset: () => {},
		activeType: null,
		setActiveType: () => {},
	}
)

export function useTemplateBuilderContext() {
	return React.use(TemplateBuilderContext)
}
